import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Container, PageTitle, GameContainer, Button, FlexContainer, ChoiceButton } from '../../styles/StyledComponents';
import { useGameContext } from '../../context/GameContext';

// Animations
const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-20px);
  }
  60% {
    transform: translateY(-10px);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const rotateIn = keyframes`
  from {
    transform: rotate(0deg) scale(0);
    opacity: 0;
  }
  to {
    transform: rotate(360deg) scale(1);
    opacity: 1;
  }
`;

// Styled Components
const Character = styled.div`
  font-size: 3.5rem;
  margin: 0.75rem;
  animation: ${float} 3s ease-in-out infinite;
  display: inline-block;

  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin: 0.5rem;
  }
`;

const MultiplicationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin: 2rem auto;
  max-width: 500px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
    margin: 1rem auto;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
  }
`;

const MultiplicationCard = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  perspective: 1000px;
  cursor: pointer;
`;

const CardInner = styled.div<{ isFlipped: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.6s;
  transform-style: preserve-3d;
  transform: ${props => props.isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'};
`;

const CardSide = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--box-shadow);
  font-size: 1.8rem;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const CardFront = styled(CardSide)`
  background-color: var(--primary-color);
  color: white;
`;

const CardBack = styled(CardSide)`
  background-color: var(--secondary-color);
  color: white;
  transform: rotateY(180deg);
`;

const QuestionContainer = styled.div`
  font-size: 2rem;
  text-align: center;
  margin: 1.5rem 0;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);

  @media (max-width: 768px) {
    font-size: 1.5rem;
    padding: 0.75rem;
    margin: 1rem 0;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
    padding: 0.5rem;
  }
`;

const AnswerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 2rem auto;
  max-width: 500px;

  @media (max-width: 768px) {
    gap: 0.75rem;
    margin: 1.5rem auto;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    margin: 1rem auto;
  }
`;

const VisualGroupContainer = styled.div`
  max-width: 100%;
  overflow-x: auto;
  margin: 1rem 0;

  @media (max-width: 768px) {
    max-height: 200px;
    overflow-y: auto;
  }
`;

const VisualGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 0.75rem 0;
  gap: 0.5rem;

  @media (max-width: 768px) {
    margin: 0.5rem 0;
    gap: 0.3rem;
  }
`;

const VisualItem = styled.div`
  animation: ${rotateIn} 0.5s forwards;
  font-size: 1.5rem;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const FeedbackMessage = styled.div<{ isCorrect: boolean }>`
  font-size: 1.5rem;
  text-align: center;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: var(--border-radius);
  background-color: ${props => props.isCorrect ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)'};
  color: ${props => props.isCorrect ? 'var(--success-color)' : 'var(--error-color)'};

  @media (max-width: 768px) {
    font-size: 1.25rem;
    padding: 0.75rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    padding: 0.5rem;
    margin: 0.75rem 0;
  }
`;

const ScoreDisplay = styled.div`
  font-size: 1.2rem;
  text-align: right;
  margin-bottom: 1rem;

  span {
    font-weight: bold;
    color: var(--primary-color);
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 0.75rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }
`;

const ResponsivePageTitle = styled(PageTitle)`
  @media (max-width: 768px) {
    font-size: 2rem;
    margin: 1rem 0;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
    margin: 0.75rem 0;
  }
`;

const ResponsiveGameContainer = styled(GameContainer)`
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 1.5rem 0;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    margin: 1rem 0;
  }
`;

const TimesTablesHeading = styled.h2`
  font-size: 1.8rem;
  text-align: center;
  margin: 1.5rem 0 1rem;
  color: var(--primary-color);

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin: 1rem 0 0.75rem;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
    margin: 0.75rem 0 0.5rem;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 10px;
  background-color: #e0e0e0;
  border-radius: 5px;
  margin: 1rem 0;
  overflow: hidden;

  @media (max-width: 480px) {
    height: 6px;
    margin: 0.5rem 0;
  }
`;

const Progress = styled.div<{ width: number }>`
  height: 100%;
  width: ${props => props.width}%;
  background-color: var(--success-color);
  transition: width 0.3s ease;
`;

// Emojis to represent multiplication
const itemEmojis = ['🍎', '🍌', '🍒', '🍓', '🍊', '🍇', '🍉', '🌟', '🎈', '🎁', '🧸', '🦄'];

interface Question {
    num1: number;
    num2: number;
    choices: number[];
    correctAnswer: number;
}

const generateQuestion = (maxNumber: number = 5): Question => {
    // For a 5-year-old, let's keep multiplication simple (2-5 range initially)
    const num1 = Math.floor(Math.random() * maxNumber) + 1;
    const num2 = Math.floor(Math.random() * maxNumber) + 1;

    const correctAnswer = num1 * num2;

    // Generate answer choices with the correct answer among them
    const choices = [correctAnswer];

    while (choices.length < 4) {
        // Generate plausible wrong answers (close to the correct answer)
        let randomChoice;
        const randomMethod = Math.random();

        if (randomMethod < 0.33) {
            // Add or subtract 1 or 2
            randomChoice = correctAnswer + (Math.floor(Math.random() * 3) + 1) * (Math.random() > 0.5 ? 1 : -1);
        } else if (randomMethod < 0.66) {
            // Use a number from another multiplication in the range
            const wrongNum1 = Math.floor(Math.random() * maxNumber) + 1;
            const wrongNum2 = Math.floor(Math.random() * maxNumber) + 1;
            randomChoice = wrongNum1 * wrongNum2;
        } else {
            // Addition instead of multiplication
            randomChoice = num1 + num2;
        }

        // Ensure the choice is positive and not already in the list
        if (randomChoice > 0 && !choices.includes(randomChoice)) {
            choices.push(randomChoice);
        }
    }

    // Shuffle the choices
    for (let i = choices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [choices[i], choices[j]] = [choices[j], choices[i]];
    }

    return {
        num1,
        num2,
        choices,
        correctAnswer
    };
};

const TimesTablesAdventure: React.FC = () => {
    const [question, setQuestion] = useState<Question>(generateQuestion());
    const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
    const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
    const [score, setScore] = useState<number>(0);
    const [level, setLevel] = useState<number>(1);
    const [showVisual, setShowVisual] = useState<boolean>(true);
    const [maxNumber, setMaxNumber] = useState<number>(5);
    const [flippedCards, setFlippedCards] = useState<{ [key: string]: boolean }>({});
    const { updateProgress } = useGameContext();

    // Increase difficulty as the player scores more points
    useEffect(() => {
        if (score >= 5 && level === 1) {
            setLevel(2);
            setMaxNumber(8);
        } else if (score >= 15 && level === 2) {
            setLevel(3);
            setMaxNumber(12);
        }
    }, [score, level]);

    // Generate a new question
    const generateNewQuestion = () => {
        setQuestion(generateQuestion(maxNumber));
        setSelectedAnswer(null);
        setIsCorrect(null);
        setShowVisual(true);
        setFlippedCards({});
    };

    const handleAnswerSelection = (answer: number) => {
        if (selectedAnswer !== null) return; // Prevent selecting another answer

        setSelectedAnswer(answer);
        const correct = answer === question.correctAnswer;
        setIsCorrect(correct);

        if (correct) {
            setScore(prevScore => prevScore + 1);
            playSound('success');
        } else {
            playSound('error');
        }

        // Save progress to context
        updateProgress('times-tables-adventure', score + (correct ? 1 : 0));

        // Move to next question after 3 seconds
        setTimeout(() => {
            generateNewQuestion();
        }, 3000);
    };

    const playSound = (type: 'success' | 'error' | 'click') => {
        // In a real implementation, we would play actual sounds here
        console.log(`Playing ${type} sound`);
    };

    const handleCardFlip = (cardId: string) => {
        setFlippedCards({
            ...flippedCards,
            [cardId]: !flippedCards[cardId]
        });
    };

    const getQuestionText = () => {
        const { num1, num2 } = question;
        const emoji = itemEmojis[Math.floor(Math.random() * itemEmojis.length)];

        return `How many ${emoji} are there in ${num1} groups of ${num2}?`;
    };

    const renderVisualMultiplication = () => {
        const { num1, num2 } = question;
        const emoji = itemEmojis[Math.floor(Math.random() * itemEmojis.length)];

        return (
            <VisualGroupContainer>
                {Array.from({ length: num1 }).map((_, groupIndex) => (
                    <VisualGroup key={groupIndex}>
                        {Array.from({ length: num2 }).map((_, itemIndex) => (
                            <VisualItem
                                key={`${groupIndex}-${itemIndex}`}
                                style={{ animationDelay: `${(groupIndex * num2 + itemIndex) * 0.1}s` }}
                            >
                                {emoji}
                            </VisualItem>
                        ))}
                    </VisualGroup>
                ))}
            </VisualGroupContainer>
        );
    };

    const renderTimesTablesCards = () => {
        const cards = [];
        for (let i = 1; i <= 3; i++) {
            for (let j = 1; j <= 3; j++) {
                const cardId = `card-${i}-${j}`;
                cards.push(
                    <MultiplicationCard key={cardId} onClick={() => handleCardFlip(cardId)}>
                        <CardInner isFlipped={flippedCards[cardId] || false}>
                            <CardFront>{i} × {j}</CardFront>
                            <CardBack>{i * j}</CardBack>
                        </CardInner>
                    </MultiplicationCard>
                );
            }
        }
        return cards;
    };

    // Determine if we should show a smaller grid for very small screens
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const renderResponsiveTimesTablesCards = () => {
        // For very small screens, only show a 2x2 grid instead of 3x3
        const maxIndex = windowWidth <= 380 ? 2 : 3;

        const cards = [];
        for (let i = 1; i <= maxIndex; i++) {
            for (let j = 1; j <= maxIndex; j++) {
                const cardId = `card-${i}-${j}`;
                cards.push(
                    <MultiplicationCard key={cardId} onClick={() => handleCardFlip(cardId)}>
                        <CardInner isFlipped={flippedCards[cardId] || false}>
                            <CardFront>{i} × {j}</CardFront>
                            <CardBack>{i * j}</CardBack>
                        </CardInner>
                    </MultiplicationCard>
                );
            }
        }
        return cards;
    };

    return (
        <Container>
            <ResponsivePageTitle>Times Tables Adventure</ResponsivePageTitle>

            <ResponsiveGameContainer>
                <FlexContainer justify="space-between">
                    <Link to="/">
                        <Button variant="secondary">Back</Button>
                    </Link>

                    <ScoreDisplay>
                        Level: <span>{level}</span> | Score: <span>{score}</span>
                    </ScoreDisplay>
                </FlexContainer>

                <ProgressBar>
                    <Progress width={(level === 1 ? score / 5 * 100 : level === 2 ? (score - 5) / 10 * 100 : (score - 15) / 20 * 100)} />
                </ProgressBar>

                <FlexContainer>
                    <Character>🧙</Character>
                </FlexContainer>

                <QuestionContainer>
                    {getQuestionText()}
                </QuestionContainer>

                {showVisual && (
                    <FlexContainer>
                        {renderVisualMultiplication()}
                    </FlexContainer>
                )}

                <AnswerGrid>
                    {question.choices.map((choice, index) => (
                        <ChoiceButton
                            key={index}
                            isSelected={selectedAnswer === choice}
                            isCorrect={selectedAnswer === choice && isCorrect === true}
                            onClick={() => handleAnswerSelection(choice)}
                            disabled={selectedAnswer !== null}
                        >
                            {choice}
                        </ChoiceButton>
                    ))}
                </AnswerGrid>

                {isCorrect !== null && (
                    <FeedbackMessage isCorrect={isCorrect}>
                        {isCorrect
                            ? '✨ Great job! That\'s correct! ✨'
                            : `Oops! The correct answer is ${question.correctAnswer}.`}
                    </FeedbackMessage>
                )}

                {level === 1 && (
                    <>
                        <TimesTablesHeading>Practice with Times Tables</TimesTablesHeading>
                        <MultiplicationGrid>
                            {renderResponsiveTimesTablesCards()}
                        </MultiplicationGrid>
                    </>
                )}
            </ResponsiveGameContainer>
        </Container>
    );
};

export default TimesTablesAdventure;
